import { useState } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Button from "@mui/material/Button";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { alpha } from "@mui/material/styles";
import {
  X, LogIn, PlusCircle, MapPin, Compass, Sparkles, Info, HelpCircle, Settings, Moon, Sun,
  ChevronDown, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { BrandLogo } from "@/components/brand/Logo";
import Eyebrow from "@/components/common/Eyebrow";
import api from "@/lib/api";
import { toast } from "sonner";
import { WHY_NIRVAGOO, FAQS, CTA_IMAGE } from "@/constants/homeContent";

const NAV_LINKS = [
  { label: "Stays", to: "/villas", Icon: Compass },
  { label: "Destinations", to: "/#destinations", Icon: MapPin },
  { label: "Inspiration", to: "/#inspiration", Icon: Sparkles },
  { label: "Why Nirvagoo", to: "/#why-nirvagoo", Icon: Info },
  { label: "Help Centre", to: "/p/help", Icon: HelpCircle },
];

export default function MobileMenu({ open, onClose }) {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const [email, setEmail] = useState("");

  const submitNewsletter = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); toast("Welcome aboard — check your inbox."); setEmail(""); }
    catch { toast("Couldn't subscribe. Try again."); }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} data-testid="mobile-menu" PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2.5, borderBottom: 1, borderColor: "divider" }}>
        <BrandLogo size={24} />
        <IconButton onClick={onClose} data-testid="mobile-menu-close"><X size={20} /></IconButton>
      </Box>

      <Box sx={{ overflowY: "auto", flex: 1 }}>
        <Box sx={{ p: 1.5 }}>
          {!user && (
            <Box component={Link} to="/login" onClick={onClose} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.5, borderRadius: 2.5, textDecoration: "none", color: "inherit", "&:hover": { bgcolor: "action.hover" } }} data-testid="mobile-menu-signin">
              <LogIn size={17} /><Typography sx={{ fontSize: 15 }}>Sign in / Sign up</Typography>
            </Box>
          )}
          <Box component={Link} to="/owner" onClick={onClose} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.5, borderRadius: 2.5, textDecoration: "none", color: "inherit", "&:hover": { bgcolor: "action.hover" } }} data-testid="mobile-menu-list-villa">
            <PlusCircle size={17} /><Typography sx={{ fontSize: 15 }}>List your villa</Typography>
          </Box>
          {NAV_LINKS.map(({ label, to, Icon }) => (
            <Box key={label} component={Link} to={to} onClick={onClose} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.5, borderRadius: 2.5, textDecoration: "none", color: "inherit", "&:hover": { bgcolor: "action.hover" } }} data-testid={`mobile-menu-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon size={17} /><Typography sx={{ fontSize: 15 }}>{label}</Typography>
            </Box>
          ))}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.5, borderRadius: 2.5 }}>
            <Settings size={17} /><Typography sx={{ fontSize: 15, flex: 1 }}>Settings</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1.5, borderRadius: 2.5 }}>
            {theme === "dark" ? <Moon size={17} /> : <Sun size={17} />}
            <Typography sx={{ fontSize: 15, flex: 1 }}>Dark mode</Typography>
            <Switch checked={theme === "dark"} onChange={toggle} data-testid="mobile-menu-theme-toggle" />
          </Box>
        </Box>

        <Box sx={{ px: 2.5, py: 2 }}>
          <Box
            component={Link}
            to="/villas"
            onClick={onClose}
            sx={{
              position: "relative", display: "block", borderRadius: 3.5, overflow: "hidden", height: 140, textDecoration: "none",
            }}
          >
            <Box component="img" src={CTA_IMAGE} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.45)" }} />
            <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "center", px: 2.5, color: "#fff" }}>
              <Typography className="font-serif-display" sx={{ fontSize: 19, lineHeight: 1.2 }}>Curated escapes. Extraordinary always.</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, fontSize: 13 }}>Explore <ArrowRight size={13} /></Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 2 }}>Why choose Nirvagoo?</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {WHY_NIRVAGOO.map(({ Icon, title, text }) => (
              <Box key={title} sx={{ display: "flex", gap: 1.5 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: (t) => alpha(t.palette.primary.main, 0.12), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={16} className="text-gold" />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{title}</Typography>
                  <Typography variant="caption" color="text.secondary">{text}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ px: 2.5, py: 2 }}>
          <Eyebrow sx={{ mb: 1 }}>Not just stays</Eyebrow>
          <Typography className="font-serif-display" sx={{ fontSize: 20, mb: 2 }}>Stories for a lifetime.</Typography>
        </Box>

        <Box sx={{ px: 2.5, py: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 1 }}>Frequently asked questions</Typography>
          {FAQS.map((f, i) => (
            <Accordion key={i} disableGutters data-testid={`mobile-faq-${i}`}>
              <AccordionSummary expandIcon={<ChevronDown size={16} />}><Typography sx={{ fontSize: 14, fontWeight: 500 }}>{f.q}</Typography></AccordionSummary>
              <AccordionDetails><Typography variant="body2" color="text.secondary">{f.a}</Typography></AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Box sx={{ px: 2.5, py: 3, bgcolor: "action.hover" }}>
          <Typography sx={{ fontWeight: 600, fontSize: 15, mb: 0.5 }}>Get travel ideas, new villas and special offers.</Typography>
          <Box component="form" onSubmit={submitNewsletter} sx={{ display: "flex", gap: 1, mt: 1.5 }}>
            <Box
              component="input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
              data-testid="mobile-newsletter-input"
              sx={{ flex: 1, minWidth: 0, px: 2, py: 1.25, borderRadius: 999, border: 1, borderColor: "divider", bgcolor: "background.paper", color: "inherit", outline: "none", fontFamily: "inherit", fontSize: 14 }}
            />
            <Button type="submit" variant="contained" data-testid="mobile-newsletter-submit">Join</Button>
          </Box>
        </Box>

        <Box sx={{ px: 2.5, py: 3 }}>
          <BrandLogo size={22} />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
            {["All Villas", "Luxury", "Weekend Getaways", "Pet Friendly", "About", "Careers"].map((l) => (
              <Typography key={l} variant="caption" color="text.secondary">{l}</Typography>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            © {new Date().getFullYear()} Nirvagoo. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
}
